import * as React from 'react';
import snakeCase from 'lodash/snakeCase';

import PopoverList from 'components/popover-list/PopoverList';
import Tooltip from 'components/Tooltip';
import SVGDynamicIcon from 'components/svg-dynamic-icon/SVGDynamicIcon';

import { __t, applyTranslations } from 'utils/language';
import { getCountMetric, getRateMetric } from 'utils/metrics';
import { getIGStorySnippetTypeKey } from 'utils/reporting';
import {
  DELIVERABLE_TYPE_INSTAGRAM_REEL as IG_REEL_TYPE,
  DELIVERABLE_TYPE_INSTAGRAM_STORY as IG_STORY_TYPE,
  DELIVERABLE_TYPE_TIKTOK_VIDEO as TT_VIDEO_TYPE,
  DELIVERABLE_TYPE_YOUTUBE_VIDEO as YT_VIDEO_TYPE
} from 'utils/deliverables';

import './content-card-details-reactions.scss';

import type {
  ContentCardContentType,
  ContentCardItem
} from 'components/cards/content-card/types';
import type {
  ReportingMetaInfoIGStorySnippetType
} from 'modules/reporting/types';

applyTranslations(require('locales/common/collabary-common.i18n.json'));
applyTranslations(require('components/cards/content-card/shared/content-card-shared.i18n.json'));

type Props = {
  item: ContentCardItem,
  cardType: ContentCardContentType,
  igStorySnippetType?: ReportingMetaInfoIGStorySnippetType
};

const ICONS = {
  comments: 'speech-bubble-custom-fg',
  engagementRate: 'rotation-arrows-custom-fg',
  likes: 'heart-custom-fg',
  shares: 'arrow-curved-custom-fg',
  storiesImpressions: 'eye-custom-fg',
  views: 'eye-custom-fg',
  impressions: 'eye-custom-fg'
};

const LIKES = 'likes';
const COMMENTS = 'comments';
const VIEWS = 'views';
const IMPRESSIONS = 'impressions';
const SHARES = 'shares';
const ENGAGEMENT_RATE = 'engagementRate';

const COMMON_MSG_KEY = 'collabary_common';
const SHARED_MSG_KEY = 'content_card_shared';

const getIconTypes = (item: ContentCardItem): Array<string> => {
  if (item.type === IG_STORY_TYPE) {
    return ['storiesImpressions'];
  }

  if (item.type === TT_VIDEO_TYPE) {
    return [VIEWS, LIKES, COMMENTS, SHARES];
  }

  if (item.type === YT_VIDEO_TYPE) {
    return [VIEWS, LIKES, COMMENTS];
  }

  if (item.type === IG_REEL_TYPE) {
    return [IMPRESSIONS, LIKES, COMMENTS];
  }

  // ig post
  return [LIKES, COMMENTS, ENGAGEMENT_RATE];
};

const renderIcon = (iconType: string): JSX.Element => (
  <div
    className="content-card-details-reactions__icon"
    data-icon-type={iconType}
  >
    <SVGDynamicIcon
      name={ICONS[iconType]}
      width={16}
      height={16}
      customColor="black"
    />
  </div>
);

const renderTooltip = (
  iconType: string,
  cardType: ContentCardContentType,
  value: string | null
): JSX.Element => {
  const kpiLabel = __t(`${COMMON_MSG_KEY}.kpis.${snakeCase(iconType)}`);
  const tooltipMsg = __t(`${SHARED_MSG_KEY}.labels.of_label`, {
    kpi: kpiLabel,
    type: cardType
  });

  return (
    <Tooltip
      message={`${tooltipMsg}: ${value}`}
      width="auto-compact"
      classNameWrapper="content-card-details-reactions__tooltip"
    >
      {renderIcon(iconType)}
      <div className="content-card-details-reactions__value">
        {value}
      </div>
    </Tooltip>
  );
};

const renderPopover = (
  iconType: string,
  value: string | null,
  igStorySnippetType?: ReportingMetaInfoIGStorySnippetType
): JSX.Element => {
  const snippetType = getIGStorySnippetTypeKey(igStorySnippetType);

  return (
    <PopoverList
      list={[{
        title: __t(`${SHARED_MSG_KEY}.tooltips.verbose_descriptions.title`, {
          metric: __t(`${SHARED_MSG_KEY}.labels.impressions`),
          type: snippetType,
          value
        }),
        body: __t(`${SHARED_MSG_KEY}.tooltips.verbose_descriptions.${igStorySnippetType}`)
      }]}
      trigger={(
        <React.Fragment>
          {renderIcon(iconType)}
          <div className="content-card-details-reactions__value">
            {value}
          </div>
        </React.Fragment>
      )}
      width="wide"
      lineHeight="normal"
      classNamePopover="content-card-details-reactions__popover"
      classNameTrigger="content-card-details-reactions__popover-trigger"
    />
  );
};

const ContentCardDetailsReactions = ({
  item,
  cardType,
  igStorySnippetType
}: Props): JSX.Element => {
  const iconTypes = getIconTypes(item);
  const metrics = item.metrics || {};

  return (
    <div className="content-card-details-reactions">
      {
        iconTypes.map(iconType => {
          const value = iconType === 'engagementRate'
            ? getRateMetric(metrics[iconType])
            : getCountMetric(metrics[iconType]);

          return (
            <div
              key={`content-card-details-reactions--${iconType}-${metrics[iconType]}`}
              className="content-card-details-reactions__metric"
            >
              {
                iconType === 'storiesImpressions'
                  ? renderPopover(iconType, value, igStorySnippetType)
                  : renderTooltip(iconType, cardType, value)
              }
            </div>
          );
        })
      }
    </div>
  );
};

export default ContentCardDetailsReactions;
